/**
 * How a person who opens Snapshots signed out gets in (the owner's decision D25 of 2026-09-29): the
 * Beyond Accounts hand-off starts on arrival by itself and comes back to exactly the address opened.
 * Each departure is marked in the tab's session storage, and the sign-in screen is shown instead of
 * leaving again when the person comes back still signed out within a minute of the last departure
 * (a sign-in that cannot complete never becomes a loop) or after signing out here. Without session
 * storage a loop could not be seen, so nothing leaves by itself.
 */
export class Entry {
	static KEY = 'snapshots:entry';
	/** Milliseconds within which a departure is not started again by itself */
	static GUARD = 60_000;

	#storage: Storage | null;
	#now: () => number;

	/** @param storage The tab's session storage; `null` for none */
	constructor(storage: Storage | null = Entry.#session(), now: () => number = () => Date.now()) {
		this.#storage = storage;
		this.#now = now;
	}

	static #session(): Storage | null {
		try {
			return globalThis.sessionStorage ?? null;
		} catch {
			return null;
		}
	}

	#read(): string | null {
		try {
			return this.#storage?.getItem(Entry.KEY) ?? null;
		} catch {
			return null;
		}
	}

	#write(value: string | null): boolean {
		try {
			if (!this.#storage) return false;
			if (value === null) this.#storage.removeItem(Entry.KEY);
			else this.#storage.setItem(Entry.KEY, value);
			return true;
		} catch {
			return false;
		}
	}

	/** Whether the person signed out here and has not signed in since */
	get left(): boolean {
		const marker = this.#read();
		return marker === 'left' || marker === 'left:beyond';
	}

	/** Whether that sign-out also signed them out of Beyond, at Accounts' `/leave` (Q09) */
	get beyond(): boolean {
		return this.#read() === 'left:beyond';
	}

	/** Whether a departure to Accounts started within the last minute */
	get recent(): boolean {
		const marker = this.#read();
		return marker !== null && marker.startsWith('departed:') && this.#now() - Number(marker.slice(9)) < Entry.GUARD;
	}

	/** Whether an arrival signed out may leave for Accounts by itself */
	get automatic(): boolean {
		return Boolean(this.#storage) && !this.left && !this.recent;
	}

	/** Marks a departure now. @returns Whether it could be marked */
	depart(): boolean {
		return this.#write(`departed:${this.#now()}`);
	}

	/**
	 * Marks a sign-out here: nothing leaves by itself until the person signs in again
	 *
	 * @param beyond Whether the person goes on to sign out of Beyond at Accounts
	 */
	leave(beyond = false): void {
		this.#write(beyond ? 'left:beyond' : 'left');
	}

	/** Forgets the marker, once the person is signed in */
	clear(): void {
		if (this.#read() !== null) this.#write(null);
	}
}
